import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart';
import { Bar, BarChart, Pie, PieChart, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Edit, Calendar, MapPin, Clock, TrendingUp, Users, DollarSign, Package, Star } from 'lucide-react';
import { isMarkdownStructuredReport } from '../lib/campaignReportParser';
import { isFormDataStructured } from '../lib/campaignReportDataConverter';

type CampaignReport = {
  id: string;
  brandId: string;
  campaignName: string;
  reportType: 'new-brand' | 'existing-brand' | 'mid-campaign';
  campaignStartDate: string;
  campaignEndDate?: string;
  reportDate?: string;
  status: 'draft' | 'published' | 'archived';
  heroImages?: string | string[];
  reportData?: string;
  customReportData?: string;
  Brand?: {
    id: string;
    name: string;
  };
};

export default function AdminCustomReportView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>({});
  const [customData, setCustomData] = useState<any>({});
  const [heroImages, setHeroImages] = useState<string[]>([]);
  const [descriptionSections, setDescriptionSections] = useState<any>({});

  useEffect(() => {
    if (id) {
      loadReport();
    }
  }, [id]);

  async function loadReport() {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/admin/custom-reports/${id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);

        // Check if this is a markdown-structured report or form data and redirect to new component
        if (data.customReportData) {
          let shouldRedirect = false;
          
          // Check if it's markdown
          if (isMarkdownStructuredReport(data.customReportData)) {
            shouldRedirect = true;
          } else {
            // Check if it's form data (JSON)
            try {
              const parsed = typeof data.customReportData === 'string' 
                ? JSON.parse(data.customReportData) 
                : data.customReportData;
              if (isFormDataStructured(parsed)) {
                shouldRedirect = true;
              }
            } catch {
              // Not JSON, skip
            }
          }
          
          if (shouldRedirect) {
            navigate(`/admin/custom-reports/${id}/brand-view`, { replace: true });
            return;
          }
        }

        // Parse heroImages
        if (data.heroImages) {
          try {
            const parsed = typeof data.heroImages === 'string' ? JSON.parse(data.heroImages) : data.heroImages;
            setHeroImages(Array.isArray(parsed) ? parsed : []);
          } catch (e) {
            setHeroImages([]);
          }
        }

        // Parse reportData
        if (data.reportData) {
          try {
            const parsed = typeof data.reportData === 'string' ? JSON.parse(data.reportData) : data.reportData;
            setReportData(parsed);
          } catch (e) {
            setReportData({});
          }
        }

        // Parse customReportData
        if (data.customReportData) {
          try {
            const parsed = typeof data.customReportData === 'string' ? JSON.parse(data.customReportData) : data.customReportData;
            setCustomData(parsed);
            if (parsed.descriptionSections) {
              setDescriptionSections(parsed.descriptionSections);
            }
          } catch (e) {
            setCustomData({});
          }
        }
      } else {
        setError('Failed to load custom report');
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setError('Failed to load custom report');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="container mx-auto p-6">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="container mx-auto p-6">
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">{error || 'Custom report not found'}</p>
              <div className="flex justify-center mt-4">
                <Button onClick={() => navigate('/admin/custom-reports')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Custom Reports
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: string | number | undefined) => {
    if (!value) return '£0.00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: string | number | undefined) => {
    if (!value && value !== 0) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toLocaleString('en-GB');
  };

  const formatPercentage = (value: string | number | undefined) => {
    if (!value && value !== 0) return '0%';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  // Chart configurations
  const chartConfig: ChartConfig = {
    value: {
      label: 'Value',
      color: 'hsl(var(--chart-1))',
    },
  };

  const blueChartConfig: ChartConfig = {
    value: {
      label: 'Value',
      color: '#183df7',
    },
  };

  // Prepare chart data
  const prepareFootfallData = () => {
    const data = [];
    if (customData.footfallZoneTotal) {
      data.push({ name: 'Total Footfall', value: parseFloat(customData.footfallZoneTotal) || 0 });
    }
    if (customData.screenImpressionsTotal) {
      data.push({ name: 'Screen Impressions', value: parseFloat(customData.screenImpressionsTotal) || 0 });
    }
    if (customData.adPlayoutsTotalPlays) {
      data.push({ name: 'Ad Playouts', value: parseFloat(customData.adPlayoutsTotalPlays) || 0 });
    }
    return data;
  };

  const prepareEngagementData = () => {
    const data = [];
    if (customData.screenClicksTotal) {
      data.push({ name: 'Screen Interactions', value: parseFloat(customData.screenClicksTotal) || 0 });
    }
    if (customData.browsingSessionsTotal) {
      data.push({ name: 'Browsing Sessions', value: parseFloat(customData.browsingSessionsTotal) || 0 });
    }
    if (customData.averageBrowseTimeSeconds) {
      data.push({ name: 'Avg Browse Time (s)', value: parseFloat(customData.averageBrowseTimeSeconds) || 0 });
    }
    return data;
  };

  const prepareRevenueData = () => {
    const data = [];
    if (customData.initialBoxRevenueTotal) {
      data.push({ name: 'Initial Box', value: parseFloat(customData.initialBoxRevenueTotal) || 0 });
    }
    if (customData.subscriptionRevenueTotal) {
      data.push({ name: 'Subscription', value: parseFloat(customData.subscriptionRevenueTotal) || 0 });
    }
    if (customData.totalCampaignRevenue) {
      data.push({ name: 'Total Revenue', value: parseFloat(customData.totalCampaignRevenue) || 0 });
    }
    return data;
  };

  const prepareSalesTimeData = () => {
    const data = [];
    if (customData.salesTime10am1pm) {
      data.push({ name: '10am-1pm', value: parseFloat(customData.salesTime10am1pm) || 0 });
    }
    if (customData.salesTime1pm4pm) {
      data.push({ name: '1pm-4pm', value: parseFloat(customData.salesTime1pm4pm) || 0 });
    }
    if (customData.salesTime4pm7pm) {
      data.push({ name: '4pm-7pm', value: parseFloat(customData.salesTime4pm7pm) || 0 });
    }
    if (customData.salesTime7pm9pm) {
      data.push({ name: '7pm-9pm', value: parseFloat(customData.salesTime7pm9pm) || 0 });
    }
    return data;
  };

  const COLORS = ['#183df7', '#4a6cf7', '#7a8ff9', '#aab5fb'];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/custom-reports')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#183df7' }}>
                {report.campaignName || 'Custom Report'}
              </h1>
              {report.Brand && (
                <p className="text-muted-foreground mt-1">{report.Brand.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={report.status === 'published' ? 'default' : 'secondary'}>
              {report.status}
            </Badge>
            {report.status === 'draft' && (
              <Button
                onClick={() => navigate(`/admin/custom-reports/${id}/edit`)}
                style={{ backgroundColor: '#183df7', boxShadow: '0 2px 4px rgba(24, 61, 247, 0.3)' }}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Hero Images */}
        {heroImages && heroImages.length > 0 && (
          <div className="mb-8">
            <div className={`grid gap-4 ${heroImages.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {heroImages.map((img, idx) => (
                <Card key={idx} className="overflow-hidden" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                  <img
                    src={img}
                    alt={`Hero ${idx + 1}`}
                    className="w-full h-auto object-cover"
                    style={{ maxHeight: '400px' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Campaign Details */}
        {(customData.location || customData.duration || customData.productName) && (
          <Card className="mb-8" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Campaign Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {customData.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-1" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-semibold">{customData.location}</p>
                    </div>
                  </div>
                )}
                {customData.duration && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 mt-1" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Duration</p>
                      <p className="font-semibold">
                        {customData.duration} {customData.durationUnit || 'months'}
                      </p>
                    </div>
                  </div>
                )}
                {customData.productName && (
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 mt-1" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Product</p>
                      <p className="font-semibold">
                        {customData.productName}
                        {customData.productPrice && ` - ${formatCurrency(customData.productPrice)}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {customData.productStructure && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-2">Product Structure</p>
                  <p className="text-sm whitespace-pre-line">{customData.productStructure}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Key Metrics - Text Display */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Footfall & Visibility */}
          {(customData.footfallZoneTotal || customData.screenImpressionsTotal) && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#183df7' }}>Footfall & Visibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customData.footfallZoneTotal && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Footfall</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.footfallZoneTotal)}
                    </p>
                  </div>
                )}
                {customData.screenImpressionsTotal && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Screen Impressions</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.screenImpressionsTotal)}
                    </p>
                  </div>
                )}
                {customData.adPlayoutsTotalPlays && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Ad Playouts</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.adPlayoutsTotalPlays)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Engagement Metrics */}
          {(customData.screenClicksTotal || customData.browsingSessionsTotal) && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#183df7' }}>Engagement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customData.screenClicksTotal && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Screen Interactions</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.screenClicksTotal)}
                    </p>
                  </div>
                )}
                {customData.browsingSessionsTotal && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Browsing Sessions</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.browsingSessionsTotal)}
                    </p>
                  </div>
                )}
                {customData.averageBrowseTimeSeconds && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Avg Browse Time</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.averageBrowseTimeSeconds)}s
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Conversion Performance */}
          {(customData.conversionFootfallToPurchase || customData.conversionBrowsingToPurchase || customData.totalUnits) && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#183df7' }}>Conversion</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customData.conversionFootfallToPurchase && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Footfall → Purchase</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatPercentage(customData.conversionFootfallToPurchase)}
                    </p>
                  </div>
                )}
                {customData.conversionBrowsingToPurchase && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Browsing → Purchase</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatPercentage(customData.conversionBrowsingToPurchase)}
                    </p>
                  </div>
                )}
                {customData.totalUnits && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Units Sold</p>
                    <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(customData.totalUnits)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Revenue Performance */}
        {(customData.totalCampaignRevenue || customData.initialBoxRevenueTotal || customData.subscriptionRevenueTotal) && (
          <Card className="mb-8" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Revenue Performance</CardTitle>
            </CardHeader>
            <CardContent>
              {prepareRevenueData().length > 0 ? (
                <div className="space-y-6">
                  <ChartContainer config={blueChartConfig} className="h-[350px]">
                    <BarChart data={prepareRevenueData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 14, fill: '#183df7', fontWeight: 500 }}
                        axisLine={{ stroke: '#183df7', strokeWidth: 1 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 14, fill: '#183df7', fontWeight: 500 }}
                        axisLine={{ stroke: '#183df7', strokeWidth: 1 }}
                        tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                      />
                      <ChartTooltip 
                        content={<ChartTooltipContent />}
                        formatter={(value: any) => formatCurrency(value)}
                        labelStyle={{ color: '#183df7', fontWeight: 600 }}
                      />
                      <Bar dataKey="value" fill="#183df7" radius={[8, 8, 0, 0]} barSize={80} />
                    </BarChart>
                  </ChartContainer>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t">
                    {customData.initialBoxRevenueTotal && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Initial Box Revenue</p>
                        <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                          {formatCurrency(customData.initialBoxRevenueTotal)}
                        </p>
                      </div>
                    )}
                    {customData.subscriptionRevenueTotal && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Subscription Revenue</p>
                        <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                          {formatCurrency(customData.subscriptionRevenueTotal)}
                        </p>
                      </div>
                    )}
                    {customData.totalCampaignRevenue && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Total Campaign Revenue</p>
                        <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                          {formatCurrency(customData.totalCampaignRevenue)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {customData.initialBoxRevenueTotal && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Initial Box Revenue</p>
                      <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                        {formatCurrency(customData.initialBoxRevenueTotal)}
                      </p>
                    </div>
                  )}
                  {customData.subscriptionRevenueTotal && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Subscription Revenue</p>
                      <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                        {formatCurrency(customData.subscriptionRevenueTotal)}
                      </p>
                    </div>
                  )}
                  {customData.totalCampaignRevenue && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Total Campaign Revenue</p>
                      <p className="text-3xl font-bold" style={{ color: '#183df7' }}>
                        {formatCurrency(customData.totalCampaignRevenue)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Additional Metrics Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Retention & Churn */}
          {(customData.threeMonthCompletionRate || customData.churnRate || customData.averageLifetimeValue) && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#183df7' }}>Retention & Churn</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customData.threeMonthCompletionRate && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm text-muted-foreground">3-Month Completion Rate</p>
                      <p className="text-lg font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.threeMonthCompletionRate)}
                      </p>
                    </div>
                    <Progress 
                      value={parseFloat(customData.threeMonthCompletionRate) || 0} 
                      className="h-2"
                      style={{ backgroundColor: 'rgba(24, 61, 247, 0.1)' }}
                    />
                  </div>
                )}
                {customData.churnRate && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm text-muted-foreground">Churn Rate</p>
                      <p className="text-lg font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.churnRate)}
                      </p>
                    </div>
                    <Progress 
                      value={parseFloat(customData.churnRate) || 0} 
                      className="h-2"
                      style={{ backgroundColor: 'rgba(24, 61, 247, 0.1)' }}
                    />
                  </div>
                )}
                {customData.averageLifetimeValue && (
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Lifetime Value</p>
                    <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                      {formatCurrency(customData.averageLifetimeValue)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Store Halo Effect */}
          {(customData.footfallUplift || customData.estimatedIncrementalStoreRevenue) && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: '#183df7' }}>Store Halo Effect</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customData.footfallUplift && (
                  <div>
                    <p className="text-sm text-muted-foreground">Footfall Uplift</p>
                    <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                      {formatPercentage(customData.footfallUplift)}
                    </p>
                  </div>
                )}
                {customData.estimatedIncrementalStoreRevenue && (
                  <div>
                    <p className="text-sm text-muted-foreground">Incremental Store Revenue</p>
                    <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                      {formatCurrency(customData.estimatedIncrementalStoreRevenue)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sales by Time of Day */}
        {(customData.salesTime10am1pm || customData.salesTime1pm4pm || customData.salesTime4pm7pm || customData.salesTime7pm9pm) && (
          <Card className="mb-8" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Sales by Time of Day</CardTitle>
            </CardHeader>
            <CardContent>
              {prepareSalesTimeData().length > 0 ? (
                <div className="space-y-6">
                  <ChartContainer config={blueChartConfig} className="h-[350px]">
                    <BarChart data={prepareSalesTimeData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 14, fill: '#183df7', fontWeight: 500 }}
                        axisLine={{ stroke: '#183df7', strokeWidth: 1 }}
                      />
                      <YAxis 
                        tick={{ fontSize: 14, fill: '#183df7', fontWeight: 500 }}
                        axisLine={{ stroke: '#183df7', strokeWidth: 1 }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <ChartTooltip 
                        content={<ChartTooltipContent />}
                        formatter={(value: any) => `${value.toFixed(2)}%`}
                        labelStyle={{ color: '#183df7', fontWeight: 600 }}
                      />
                      <Bar dataKey="value" fill="#183df7" radius={[8, 8, 0, 0]} barSize={80} />
                    </BarChart>
                  </ChartContainer>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                    {customData.salesTime10am1pm && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">10am - 1pm</p>
                        <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                          {formatPercentage(customData.salesTime10am1pm)}
                        </p>
                      </div>
                    )}
                    {customData.salesTime1pm4pm && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">1pm - 4pm</p>
                        <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                          {formatPercentage(customData.salesTime1pm4pm)}
                        </p>
                      </div>
                    )}
                    {customData.salesTime4pm7pm && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">4pm - 7pm</p>
                        <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                          {formatPercentage(customData.salesTime4pm7pm)}
                        </p>
                      </div>
                    )}
                    {customData.salesTime7pm9pm && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">7pm - 9pm</p>
                        <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                          {formatPercentage(customData.salesTime7pm9pm)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {customData.salesTime10am1pm && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">10am - 1pm</p>
                      <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.salesTime10am1pm)}
                      </p>
                    </div>
                  )}
                  {customData.salesTime1pm4pm && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">1pm - 4pm</p>
                      <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.salesTime1pm4pm)}
                      </p>
                    </div>
                  )}
                  {customData.salesTime4pm7pm && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">4pm - 7pm</p>
                      <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.salesTime4pm7pm)}
                      </p>
                    </div>
                  )}
                  {customData.salesTime7pm9pm && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">7pm - 9pm</p>
                      <p className="text-xl font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.salesTime7pm9pm)}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Feedback */}
        {(customData.overallExperienceScore || customData.wouldRecommend || customData.commonFeedbackThemes) && (
          <Card className="mb-8" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Customer Feedback</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                {customData.overallExperienceScore && (
                  <div className="flex items-center gap-3">
                    <Star className="w-6 h-6" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Experience Score</p>
                      <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                        {customData.overallExperienceScore} / 5
                      </p>
                    </div>
                  </div>
                )}
                {customData.easeOfPurchaseScore && (
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-6 h-6" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Ease of Purchase</p>
                      <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                        {customData.easeOfPurchaseScore} / 5
                      </p>
                    </div>
                  </div>
                )}
                {customData.wouldRecommend && (
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6" style={{ color: '#183df7' }} />
                    <div>
                      <p className="text-sm text-muted-foreground">Would Recommend</p>
                      <p className="text-2xl font-bold" style={{ color: '#183df7' }}>
                        {formatPercentage(customData.wouldRecommend)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {customData.commonFeedbackThemes && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-semibold mb-2">Common Feedback Themes</p>
                  <p className="text-sm whitespace-pre-line">{customData.commonFeedbackThemes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Description Sections */}
        {(descriptionSections.executiveSummary || descriptionSections.keyInsights || descriptionSections.recommendations || descriptionSections.strategicTakeaways) && (
          <div className="space-y-6 mb-8">
            {descriptionSections.executiveSummary && (
              <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#183df7' }}>Executive Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{descriptionSections.executiveSummary}</p>
                </CardContent>
              </Card>
            )}

            {descriptionSections.keyInsights && (
              <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#183df7' }}>Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{descriptionSections.keyInsights}</p>
                </CardContent>
              </Card>
            )}

            {descriptionSections.recommendations && (
              <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#183df7' }}>Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{descriptionSections.recommendations}</p>
                </CardContent>
              </Card>
            )}

            {descriptionSections.strategicTakeaways && (
              <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#183df7' }}>Strategic Takeaways</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{descriptionSections.strategicTakeaways}</p>
                </CardContent>
              </Card>
            )}

            {descriptionSections.customerFeedback && (
              <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#183df7' }}>Customer Feedback Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-line">{descriptionSections.customerFeedback}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Additional existing report data */}
        {reportData && Object.keys(reportData).length > 0 && (
          <Card className="mb-8" style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle style={{ color: '#183df7' }}>Additional Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {reportData.totalImpressions && (
                  <div>
                    <p className="text-sm text-muted-foreground">Total Impressions</p>
                    <p className="text-lg font-bold" style={{ color: '#183df7' }}>
                      {formatNumber(reportData.totalImpressions)}
                    </p>
                  </div>
                )}
                {reportData.overallSalesPercentIncrease && (
                  <div>
                    <p className="text-sm text-muted-foreground">Sales % Increase</p>
                    <p className="text-lg font-bold" style={{ color: '#183df7' }}>
                      {formatPercentage(reportData.overallSalesPercentIncrease)}
                    </p>
                  </div>
                )}
                {reportData.totalCampaignBudget && (
                  <div>
                    <p className="text-sm text-muted-foreground">Campaign Budget</p>
                    <p className="text-lg font-bold" style={{ color: '#183df7' }}>
                      {formatCurrency(reportData.totalCampaignBudget)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
